export function getCompositePreCalcs(
  // Pwf
  wellFlowingPressureTestPSIA: number,
  // Pr
  reservoirPressurePSIA: number,
  // Pb
  bubblePointPressurePSIA: number,
  // Test Rate
  liquidFlowRateTestSTBD: number,
  // Water Cut
  waterCutRatio: number
) {
  // The current spreadsheet defining these calculations is located here:
  // equivalent spreadsheet field names are denoted above all variables
  // static/Composite_V2__1_.xlsx
  // ALL FORMULAS IN THIS FUNCTION ARE IDENTICAL ON THE 'Solve for Rate' AND 'Solve for Pressure' SHEETS
  // snake_case variable names align with spreadsheet unless otherwise noted

  // Pr
  const p_r = reservoirPressurePSIA;
  // Pwf
  const p_wf_test = wellFlowingPressureTestPSIA;

  // Test Rate
  const q_t_test = liquidFlowRateTestSTBD;
  // Water Fraction - Fw (manually calculated in spreadsheet)
  const f_w = waterCutRatio;
  // Oil Fraction - Fo (manually calculated in spreadsheet)
  const f_o = 1 - f_w;

  const p_b = bubblePointPressurePSIA > p_r ? p_r : bubblePointPressurePSIA;

  // BELOW HERE ARE THE CALCULATIONS

  // standalone A
  const a = 1 - 0.2 * (p_wf_test / p_b) - 0.8 * (p_wf_test / p_b) ** 2;

  // solve for rate::: (pwfg < p_wf && p_wf <= p_b) && (b === 0) or waterCutRatio === 0
  const j =
    p_wf_test < p_b
      ? q_t_test /
        (f_o * (p_r - p_b + (p_b * a) / 1.8) + f_w * (p_r - p_wf_test))
      : q_t_test / (p_r - p_wf_test);

  const q_b = j * (p_r - p_b);
  const q_o_max = q_b + (j * p_b) / 1.8;

  const A = (p_wf_test + 0.125 * f_o * p_b - f_w * p_r) / (0.125 * f_o * p_b);
  const B = f_w / (0.125 * f_o * p_b * j);
  const C = 2 * A * B + 80 / (q_o_max - q_b);
  const D = A ** 2 - (80 * q_b) / (q_o_max - q_b) - 81;

  const pwfb =
    0.125 *
    p_b *
    (-1 + Math.sqrt(81 - 80 * ((0.999 * q_o_max - q_b) / (q_o_max - q_b))));
  const pwfc = f_w * (p_r - q_o_max / j);
  const pwfe = p_r - (0.999 * q_o_max) / j;
  const pwfd = f_o * pwfb + f_w * pwfe;
  const pwfg = f_w * (p_r - q_o_max / j);

  const cd =
    f_w * ((0.001 * q_o_max) / j) +
    f_o *
      0.125 *
      p_b *
      (-1 + Math.sqrt(81 - 80 * ((0.999 * q_o_max - q_b) / (q_o_max - q_b))));

  const cg = 0.001 * q_o_max;

  const tan_b = cd / cg;
  const tan_a = cg / cd;
  const q_t_max = q_o_max + f_w * (p_r - q_o_max / j) * tan_a;

  return {
    p_r,
    p_wf_test,
    q_t_test,
    f_w,
    f_o,
    p_b,
    a,
    j,
    q_b,
    q_o_max,
    A,
    B,
    C,
    D,
    pwfb,
    pwfc,
    pwfd,
    pwfe,
    pwfg,
    cd,
    cg,
    tan_b,
    tan_a,
    q_t_max
  };
}

export function getMaxFlowRateComposite(
  // value is used to select calculation from column I, J or K in spreadsheet
  desiredWellFlowingPressurePSIA: number,
  // Pwf
  wellFlowingPressureTestPSIA: number,
  // Pr
  reservoirPressurePSIA: number,
  // Pb
  bubblePointPressurePSIA: number,
  // Test Rate
  liquidFlowRateTestSTBD: number,
  // Water Cut
  waterCutRatio: number
): number {
  const p_wf = desiredWellFlowingPressurePSIA;

  const { q_b, p_r, j, q_o_max, f_w, f_o, p_b, pwfg, tan_b } =
    getCompositePreCalcs(
      wellFlowingPressureTestPSIA,
      reservoirPressurePSIA,
      bubblePointPressurePSIA,
      liquidFlowRateTestSTBD,
      waterCutRatio
    );

  // static/Composite_V2__1_.xlsx
  // ALL FORMULAS IN THIS FUNCTION ARE FROM COLUMNS I, J, or K ON 'Solve for Rate'
  if (p_b < p_wf || waterCutRatio === 1) {
    // spreadsheet column I
    return j * (p_r - p_wf);
  } else if (pwfg < p_wf && p_wf <= p_b) {
    // a,b,c,d here are calculated in the columns, so override pre-calcs
    // These must be calculated here because it is for desired pressure
    // This may be a point of confusion as these are part of pre-calcs?
    const a = (p_wf + 0.125 * f_o * p_b - f_w * p_r) / (0.125 * f_o * p_b);
    const b = f_w / (0.125 * f_o * p_b * j);
    const c = 2 * a * b + 80 / (q_o_max - q_b);
    const d = a ** 2 - (80 * q_b) / (q_o_max - q_b) - 81;
    if (b === 0) {
      return Math.abs(d / c);
    } else {
      return (-1 * c + Math.sqrt(c ** 2 - 4 * b ** 2 * d)) / (2 * b ** 2);
    }
  } else {
    // (Pwf < Pwfg)
    // spreadsheet column K default?
    return (pwfg + q_o_max * tan_b - p_wf) / tan_b;
  }
}

export function getWellFlowingPressureUsingComposite(
  // value is used to select calculation from column F, G, or H
  desiredLiquidFlowRateSTBD: number,
  // Pwf
  wellFlowingPressureTestPSIA: number,
  // Pr
  reservoirPressurePSIA: number,
  // Pb
  bubblePointPressurePSIA: number,
  // Test Rate
  liquidFlowRateTestSTBD: number,
  // Water Cut
  waterCutRatio: number
) {
  const q_t = desiredLiquidFlowRateSTBD;

  const { q_b, p_r, j, q_o_max, f_w, f_o, p_b, pwfg, tan_b } =
    getCompositePreCalcs(
      wellFlowingPressureTestPSIA,
      reservoirPressurePSIA,
      bubblePointPressurePSIA,
      liquidFlowRateTestSTBD,
      waterCutRatio
    );

  // static/Composite_V2__1_.xlsx
  // ALL FORMULAS IN THIS FUNCTION ARE FROM COLUMNS F, G, and H ON 'Solve for Pressure'
  if (q_t <= q_b || waterCutRatio === 1) {
    // spreadsheet column F
    return p_r - q_t / j;
  } else if (q_t > q_b && q_t <= q_o_max) {
    // spreadsheet column G
    return (
      f_w * (p_r - q_t / j) +
      f_o *
        0.125 *
        p_b *
        (-1 + Math.sqrt(81 - 80 * ((q_t - q_b) / (q_o_max - q_b))))
    );
  } else {
    // (q_t > q_o_max)
    // spreadsheet column H
    return pwfg + q_o_max * tan_b - q_t * tan_b;
  }
}
